// frontend/src/pages/ReportingPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-01
// Updated:      2026-09-25 12:03 BST
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Widget surface alignment fix (2026-09-25 12:03 BST):
//   - Add Widget now uses the canonical `reporting` surface rather than category-only filtering.
//   - Registry widgets assigned to Reporting through their surface metadata are now selectable.
//   - Stored widgets are restored only when they remain available on the Reporting surface.
//   - New layout items use the shared WidgetSurface layout helper while preserving page sizing defaults.
//
// Description:  Reporting dashboard page with full-width widget grid and import modal.
//
// Mobile Responsiveness (2026-09-04):
//   - Added a dedicated <=768px presentation.
//   - Mobile renders active Reporting widgets as a natural single-column stream.
//   - Mobile ordering follows the persisted desktop layout position (y, then x).
//   - Mobile viewing never writes mobile positions/sizes back to desktop layout storage.
//   - Desktop react-grid-layout behaviour remains unchanged.
//   - Add/remove widget behaviour and WidgetRegistry integration are preserved.

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";
import { markWidgetUsedIn } from "../widgets/WidgetRegistry";
import {
  createSurfaceLayoutItem,
  getSurfaceWidget,
  isWidgetAvailableOnSurface,
} from "../widgets/WidgetSurface";
import { useTheme } from "../context/ThemeContext.jsx";

const SURFACE = "reporting";
const STORAGE_KEY_LAYOUT = "reportingLayout";
const STORAGE_KEY_WIDGETS = "reportingWidgets";
const MOBILE_BREAKPOINT = 768;

const defaultLayout = [];

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", apply);
    else mediaQuery.addListener(apply);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", apply);
      else mediaQuery.removeListener(apply);
    };
  }, [breakpoint]);

  return isMobile;
}

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const ReportingPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [gridWidth, setGridWidth] = useState(1200);
  const gridRef = useRef(null);

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      setGridWidth(node.offsetWidth || 1200);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    try {
      const storedLayout = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetIds = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));

      if (Array.isArray(storedLayout) && Array.isArray(storedWidgetIds)) {
        const validWidgetIds = storedWidgetIds.filter((id) =>
          isWidgetAvailableOnSurface(SURFACE, id)
        );
        const validIdSet = new Set(validWidgetIds);
        const normalised = normaliseLayout(
          storedLayout.filter((item) => item && validIdSet.has(item.i))
        );

        const components = {};
        validWidgetIds.forEach((id) => {
          const component = resolveWidgetComponent(id);
          if (component) {
            components[id] = component;
            markWidgetUsedIn(id, SURFACE);
          }
        });

        setLayout(normalised);
        setWidgets(components);

        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(components)));
        return;
      }
    } catch {}

    setLayout(defaultLayout);
  }, []);

  const resolveWidgetComponent = (id) => {
    const match = getSurfaceWidget(SURFACE, id);
    return match?.component ? <match.component /> : null;
  };

  const resolveWidgetTitle = (id) =>
    getSurfaceWidget(SURFACE, id)?.friendly_name ||
    String(id || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const handleAddWidget = (widgetId) => {
    if (!getSurfaceWidget(SURFACE, widgetId)) {
      alert("This widget is not available on the Reporting page.");
      return;
    }

    if (widgets[widgetId]) {
      alert("Widget already exists");
      return;
    }

    const component = resolveWidgetComponent(widgetId);
    if (!component) return alert("Unknown widget");

    setWidgets((prev) => ({ ...prev, [widgetId]: component }));
    markWidgetUsedIn(widgetId, SURFACE);

    setLayout((prev) => {
      const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => l.y + l.h));
      const newY = Math.max(0, bottomY);
      const newItem = createSurfaceLayoutItem(SURFACE, widgetId, {
        x: 0,
        y: newY,
        w: 6,
        h: 4,
        minW: 4,
        minH: 3,
        maxW: 12,
      });
      const newLayout = [...prev, newItem];
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify([...Object.keys(widgets), widgetId]));
      return newLayout;
    });
    setShowAddModal(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const clone = { ...prev };
      delete clone[widgetId];
      return clone;
    });
    setLayout((prev) => {
      const filtered = prev.filter((item) => item.i !== widgetId);
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(filtered));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId)));
      return filtered;
    });
  };

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
      } catch {}
    },
    [widgets]
  );

  const mobileLayoutItems = useMemo(() => {
    const widgetIds = new Set(Object.keys(widgets));

    const positioned = layout
      .filter((item) => widgetIds.has(item.i))
      .slice()
      .sort((a, b) => {
        const ay = Number.isFinite(Number(a?.y)) ? Number(a.y) : Number.MAX_SAFE_INTEGER;
        const by = Number.isFinite(Number(b?.y)) ? Number(b.y) : Number.MAX_SAFE_INTEGER;
        if (ay !== by) return ay - by;

        const ax = Number.isFinite(Number(a?.x)) ? Number(a.x) : Number.MAX_SAFE_INTEGER;
        const bx = Number.isFinite(Number(b?.x)) ? Number(b.x) : Number.MAX_SAFE_INTEGER;
        if (ax !== bx) return ax - bx;

        return String(a?.i || "").localeCompare(String(b?.i || ""));
      });

    const positionedIds = new Set(positioned.map((item) => item.i));
    const missing = Object.keys(widgets)
      .filter((id) => !positionedIds.has(id))
      .map((id) => ({ i: id, x: 0, y: Number.MAX_SAFE_INTEGER, w: 6, h: 4 }));

    return [...positioned, ...missing];
  }, [layout, widgets]);

  const getMobileWidgetHeight = useCallback(
    (item) => {
      const h = Number.isFinite(Number(item?.h)) ? Math.max(1, Number(item.h)) : 4;
      return Math.max(280, h * rowHeight + Math.max(0, h - 1) * 24);
    },
    [rowHeight]
  );

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingLeft: isMobile ? 12 : containerPadding,
        paddingRight: isMobile ? 12 : containerPadding,
        paddingTop: isMobile ? 4 : undefined,
        paddingBottom: isMobile ? 16 : undefined,
        boxSizing: "border-box",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            height: isMobile ? "auto" : headerHeight,
            minHeight: isMobile ? 58 : undefined,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            marginBottom: isMobile ? 6 : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              margin: 0,
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              minWidth: 0,
            }}
          >
            Reporting Dashboard
          </h2>

          <button
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: theme.button_action?.normal || "#000",
              color: "#fff",
              padding: isMobile ? "10px 14px" : "10px 18px",
              minHeight: isMobile ? 44 : undefined,
              borderRadius: "6px",
              fontWeight: "bold",
              fontFamily: "var(--font-body)",
              cursor: "pointer",
              border: "none",
              transition: "background-color 0.2s ease",
              whiteSpace: "nowrap",
              flex: "0 0 auto",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.button_action?.hover || "#333")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.button_action?.normal || "#000")}
            onMouseDown={(e) => (e.currentTarget.style.backgroundColor = theme.button_action?.click || "#111")}
            onMouseUp={(e) => (e.currentTarget.style.backgroundColor = theme.button_action?.hover || "#333")}
          >
            Add Widget
          </button>
        </div>

        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
              minWidth: 0,
              paddingBottom: 8,
            }}
          >
            {mobileLayoutItems.map((item) => (
              <div
                key={item.i}
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: `${getMobileWidgetHeight(item)}px`,
                  overflow: "hidden",
                }}
              >
                <WidgetWrapper
                  title={resolveWidgetTitle(item.i)}
                  onRemove={() => handleRemoveWidget(item.i)}
                >
                  {widgets[item.i]}
                </WidgetWrapper>
              </div>
            ))}
          </div>
        ) : (
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={rowHeight}
            width={gridWidth}
            draggableHandle=".widget-drag-handle"
            resizeHandles={["se"]}
            isResizable
            isDraggable
            onLayoutChange={onLayoutChange}
            compactType={null}
            preventCollision={false}
            margin={[16, 24]}
          >
            {Object.entries(widgets).map(([key, WidgetComp]) => (
              <div key={key}>
                <WidgetWrapper
                  title={resolveWidgetTitle(key)}
                  onRemove={() => handleRemoveWidget(key)}
                >
                  {WidgetComp}
                </WidgetWrapper>
              </div>
            ))}
          </GridLayout>
        )}

        {showAddModal && (
          <AddWidgetModal
            surface={SURFACE}
            excludedWidgetIds={Object.keys(widgets)}
            onClose={() => setShowAddModal(false)}
            onSelect={handleAddWidget}
          />
        )}
      </div>
    </div>
  );
};

export default ReportingPage;
